import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { deviationJobsQuery } from '@/queries/deviation.query';
import { surfaceDifferenceJobsQuery } from '@/queries/surfaceDifference.query';
import { layersQuery } from '@/queries/layer.query';
import { getProjectJobsQuery } from '@/queries/project.query';
import { deviationTrackingAtom } from '@/atoms/deviation-progress.atom';
import { jobErrorMessage } from '@/lib/jobErrorMessage';

/**
 * Mounts once per project shell.
 *
 * Two responsibilities, deliberately decoupled:
 *
 * 1. **Toast updates** — driven by in-memory trackers populated by the
 *    deviation modal's onSuccess. Only this session's jobs get a toast,
 *    which is the desired behaviour (toast IDs are local to sonner).
 *
 * 2. **Badge count** — derived from the live job query, not the trackers,
 *    so reloading the page or starting a job in another tab still surfaces
 *    a badge if the server has anything in WAITING/PROCESSING.
 *
 * ## Polling
 *
 * We do NOT rely on TanStack Query's `refetchInterval` for the deviation /
 * layers queries here. Both query keys (`['deviationJobs', projectId]` and
 * `['layers', projectId]`) are also subscribed by other hooks on the same
 * page (`useLayers` for layers; `AnalysesPage` for deviationJobs). When
 * multiple observers share a query key, TanStack Query's interval
 * coordination is fragile — particularly when the other observers use the
 * callback form of `refetchInterval` that re-evaluates on every cycle.
 * Two earlier attempts to fix the "toast spins forever" bug by tweaking
 * `refetchInterval` here were defeated by the page-level `useLayers`
 * polling predicate returning false in its own callback.
 *
 * The reliable approach is an explicit `setInterval` driven by
 * `hasTrackers`, which calls `invalidateQueries` on both keys. The page's
 * own observers see the invalidation and refetch — we don't have to fight
 * for ownership of the polling cadence.
 */
export function useDeviationProgress(projectId: string) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(deviationTrackingAtom);
  const queryClient = useQueryClient();

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  // Read the deviation- and surface-difference-job caches to fold server-side
  // state into the polling predicate. Lets the badge survive page reloads — if
  // jobs are running server-side, we should poll even without a local tracker.
  // Note: this is a one-shot cache read; it doesn't subscribe. We re-read it on
  // every render, which is fine because the caches are updated by the page's own
  // query subscribers (e.g. AnalysesPage when mounted).
  const isRunning = (j: { status: string }) => j.status === 'WAITING' || j.status === 'PROCESSING';
  const cachedJobs = queryClient.getQueryData<{ deviationJobs: { status: string }[] }>(['deviationJobs', projectId]);
  const cachedSdJobs = queryClient.getQueryData<{ surfaceDifferenceJobs: { status: string }[] }>([
    'surfaceDifferenceJobs',
    projectId,
  ]);
  const anyServerRunning =
    (cachedJobs?.deviationJobs.some(isRunning) ?? false) ||
    (cachedSdJobs?.surfaceDifferenceJobs.some(isRunning) ?? false);

  const shouldPoll = hasTrackers || anyServerRunning;

  // Read-only subscriptions — `refetch` returned here is what we drive the
  // polling with. Each `refetch()` call hits the network directly,
  // bypassing every observer-coordination question (`invalidateQueries`
  // requires an active observer to actually fire; sharing query keys with
  // the page's own hooks made that fragile across earlier attempts).
  const { data, refetch: refetchJobs } = useQuery({
    queryKey: ['deviationJobs', projectId],
    queryFn: () => execute(deviationJobsQuery, { projectId }),
    enabled: !!projectId,
  });

  // Surface-difference runs reuse this hook's trackers (see
  // RunSurfaceDifferenceDialog). Their jobs live under a different query, so
  // the failure path needs both lists to find the tracker's batch.
  const { data: sdData, refetch: refetchSdJobs } = useQuery({
    queryKey: ['surfaceDifferenceJobs', projectId],
    queryFn: () => execute(surfaceDifferenceJobsQuery, { projectId }),
    enabled: !!projectId,
  });

  // Clearance runs (see RunClearanceDialog) reuse these trackers too, but
  // CLEARANCE has no dedicated `clearanceJobs(projectId)` resolver the way
  // deviation and surface-difference do — adding one just for a failure
  // check would be a schema change for a single boolean. Falls back to the
  // generic `jobs(projectId)` query instead (`getProjectJobsQuery`, the same
  // one `useReportProgress` polls), filtered to `type === 'CLEARANCE'` below.
  // The SUCCESS path needs it too: postprocess sets the same
  // `metadata.derivedFrom = pointcloudLayerId` on a clearance run's output
  // cloud, so the layer-scan alone cannot tell this run's output from any
  // other run's on the same source — the scan is disambiguated by
  // `metadata.batchId` and gated on this batch's POST job being PROCESSED.
  const { data: projectJobsData, refetch: refetchProjectJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId,
  });

  const { data: layersData, refetch: refetchLayers } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  // Explicit polling. While `shouldPoll` is true, fire `refetch()` directly
  // every 2 s on both queries. `refetch` ignores `enabled` and any shared
  // observer's options, so a single mounted instance of this hook fully
  // owns the cadence — regardless of what `useLayers` / `AnalysesPage` do
  // with the same query keys. The console-visible network requests are
  // proof the cadence is alive; if you don't see them in DevTools while a
  // tracker exists, the bug is elsewhere (e.g. the modal failed to add a
  // tracker, or `projectId` is empty).
  useEffect(() => {
    if (!shouldPoll || !projectId) return;
    const tick = () => {
      void refetchJobs();
      void refetchSdJobs();
      void refetchProjectJobs();
      void refetchLayers();
    };
    // Fire once immediately so we don't wait 2 s for the first tick — the
    // user just clicked Run and expects to see polling start now.
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [shouldPoll, projectId, refetchJobs, refetchSdJobs, refetchProjectJobs, refetchLayers]);

  useEffect(() => {
    if (!hasTrackers) return;

    const jobs = [
      ...(data?.deviationJobs ?? []),
      ...(sdData?.surfaceDifferenceJobs ?? []),
      ...(projectJobsData?.jobs ?? []).filter(
        (j) => j.type === 'CLEARANCE' || j.type === 'LAYER_STACK' || j.type === 'SECTION_OVERLAYS',
      ),
    ];
    // Unfiltered project job list. A clearance run is a CHAIN — CLEARANCE
    // (ECS) -> LAS child (PotreeConverter) -> POST (postprocess, which is
    // what actually creates the layer) — so the clearance branches below need
    // to see every job in the batch, not just the `type === 'CLEARANCE'`
    // parent the failure lookup above filters to.
    const allJobs = projectJobsData?.jobs ?? [];
    const layers = layersData?.layers ?? [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        /**
         * A SECTION_OVERLAYS re-sample (section-overlays spec §7) resolves
         * BEFORE any of the layer logic below, and never reaches it.
         *
         * It is the one tracked run that mints no layer: it rewrites the two
         * overlay byproducts on the run's own batch and stamps
         * `metadata.sectionOverlays` back onto the SAME result layer the dock
         * is already open on. The layer scan below asks "which NEW layer
         * appeared", which for this run is "none, ever" — so falling through
         * would leave the toast spinning until the tab was closed.
         *
         * The three invalidations are all load-bearing and all different:
         * `layers` refreshes the metadata block (the new `settingsHash`),
         * `batchByproducts` refreshes the signed URLs the chart fetches the
         * file through, and `sectionOverlays` drops the decoded file itself —
         * which is cached with `staleTime: Infinity` under the OLD filenames'
         * key and would otherwise never be re-read.
         */
        if (tracker.kind === 'sectionOverlays') {
          const own = allJobs.filter((j) => j.batchId === tracker.batchId && j.type === 'SECTION_OVERLAYS');
          const failed = own.find((j) => j.status === 'FAILED');
          const done = own.find((j) => j.status === 'PROCESSED');
          if (failed) {
            toast.error(jobErrorMessage(failed.errorLogs) ?? t('stationVolumes.section.overlays.failedToast'), {
              id: tracker.toastId,
              duration: 8000,
            });
            next.splice(i, 1);
            changed = true;
            continue;
          }
          if (done) {
            toast.success(t('stationVolumes.section.overlays.completedToast'), {
              id: tracker.toastId,
              duration: 4000,
            });
            queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
            if (tracker.runBatchId) queryClient.invalidateQueries({ queryKey: ['batchByproducts', tracker.runBatchId] });
            queryClient.invalidateQueries({ queryKey: ['sectionOverlays'] });
            next.splice(i, 1);
            changed = true;
          }
          continue;
        }

        // A clearance run is the only kind whose completion is judged on the
        // whole job chain (see the success path below), so it is also the only
        // kind that must fail on ANY job in its batch: with the POST gate in
        // place, a LAS child or a postprocess that dies would otherwise leave
        // the toast spinning forever, because the CLEARANCE parent this hook
        // used to watch alone had already succeeded.
        const isClearance = tracker.kind === 'clearance';
        // A LAYER_STACK run is judged on the same whole-chain basis, and for
        // the same reason: the LAYER_STACK job succeeding only means the
        // worker finished, not that postprocess has minted the result layer
        // (spec §6.2). Watching the parent alone would resolve the toast a
        // beat before there is anything in the tree to click, or leave it
        // spinning for ever if postprocess itself died.
        const isStack = tracker.kind === 'layerStack';
        const wholeChain = isClearance || isStack;
        const batchJobs = wholeChain ? allJobs.filter((j) => j.batchId === tracker.batchId) : [];

        // Failure path: short-circuit on a FAILED deviation job.
        const job = wholeChain
          ? batchJobs.find((j) => j.status === 'FAILED')
          : jobs.find((j) => j.batchId === tracker.batchId);
        if (job?.status === 'FAILED') {
          // `deviationJobs` / `surfaceDifferenceJobs` / the unfiltered
          // project job list all select `errorLogs` now, so it arrives on
          // `job` itself — read it directly rather than looking the same job
          // up by id in a second query's result. Fix round 1: a cross-query
          // lookup here raced the effect's own dependency list (`data` and
          // `projectJobsData` are independent deps at the bottom of this
          // effect) — when the deviation/surface-difference query settled
          // first, the tracker was already spliced out by the time
          // `projectJobsData` caught up, and the sentence was lost for good.
          const specific = jobErrorMessage(job.errorLogs);
          toast.error(
            specific ??
              t(
                tracker.kind === 'surfaceDifference'
                  ? 'surfaceDifference.failedToast'
                  : tracker.kind === 'clearance'
                    ? 'clearance.dialog.failedToast'
                    : tracker.kind === 'layerStack'
                      ? 'layerStack.dialog.failedToast'
                      : 'deviationAnalysis.failedToast',
                { name: tracker.sourceLayerName },
              ),
            // A sentence the user must act on needs longer than the 5 s a
            // generic "something failed" toast gets.
            { id: tracker.toastId, duration: specific ? 12000 : 5000 },
          );
          next.splice(i, 1);
          changed = true;
          continue;
        }

        // Success path: the LAYER produced by postprocess has appeared.
        // Match by metadata.derivedFrom (set by postprocess from the
        // deviationContext). Disambiguate against pre-existing derived layers
        // by checking the snapshot the modal captured — the new layer is one
        // whose id isn't in `existingDerivedLayerIds`. We deliberately do NOT
        // compare createdAt to a client-captured `startedAt` because the two
        // clocks can disagree by several seconds, which previously caused the
        // toast to spin forever (client ahead) or resolve against a stale
        // layer (client behind).
        //
        // `derivedFrom` + the snapshot alone is a LAYER-SHAPE test, not a
        // THIS-RUN test: every clearance / deviation / ground-classify /
        // rainfall run on the same cloud stamps the same `derivedFrom`, and
        // the snapshot only knows what the client had already fetched when
        // Run was clicked (`useLayers` refetches on mount + invalidation, it
        // does not poll). So a derived layer belonging to a DIFFERENT run —
        // one produced by a run started moments earlier, or one that landed
        // between the last layer fetch and the click — passed both halves and
        // flipped the toast to "complete" while this run's CLEARANCE job was
        // still on ECS. Clearance runs therefore also match on
        // `metadata.batchId`, which postprocess stamps from the leaf job
        // (`metadata.batchId = leafJob.batchId`, postprocess.ts) and which is
        // exactly the batch id `startClearance` returned to the dialog.
        // Treated as a filter only WHEN PRESENT so a layer written by a server
        // build that predates that stamp still resolves rather than hanging.
        const existingIds = new Set(tracker.existingDerivedLayerIds);
        const newLayer = layers.find((l) => {
          const meta = (l.metadata ?? {}) as { derivedFrom?: string; batchId?: string; type?: string };
          // A stack has N source surfaces and no single layer to be derived
          // FROM, so `postprocess.ts` stamps no `derivedFrom` on the one
          // layer it mints. The batch id it does stamp is the id
          // `startLayerStack` returned to the dialog, which identifies THIS
          // run exactly — a stricter test than `derivedFrom` ever was.
          if (isStack) return meta.batchId === tracker.batchId && meta.type === 'LAYER_STACK' && !existingIds.has(l.id);
          if (meta.derivedFrom !== tracker.sourceLayerId) return false;
          if (existingIds.has(l.id)) return false;
          if (isClearance && typeof meta.batchId === 'string' && meta.batchId !== tracker.batchId) return false;
          return true;
        });
        // ...and the chain has to be FINISHED, not merely far enough along to
        // have inserted the layer row. postprocess inserts the layers first
        // and only then marks its own POST job PROCESSED and the batch
        // PROCESSED (postprocess.ts, `job.completeSuccess` at the end), so
        // "the batch's POST job is PROCESSED" is the one client-visible signal
        // that means postprocess itself is done. The POST job does not exist
        // at all until the batch's last leaf finishes, so an empty/POST-less
        // batch view can never be mistaken for a finished one.
        const chainFinished = !wholeChain || batchJobs.some((j) => j.type === 'POST' && j.status === 'PROCESSED');
        if (newLayer && chainFinished) {
          toast.success(
            t(
              tracker.kind === 'surfaceDifference'
                ? 'surfaceDifference.completedToast'
                : tracker.kind === 'clearance'
                  ? 'clearance.dialog.completedToast'
                  : tracker.kind === 'layerStack'
                    ? 'layerStack.dialog.completedToast'
                    : 'deviationAnalysis.completedToast',
              { name: tracker.sourceLayerName },
            ),
            { id: tracker.toastId, duration: 5000 },
          );
          queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [data, sdData, projectJobsData, layersData, hasTrackers, projectId, queryClient, setTrackers, t]);

  // Badge derives from server state so it survives reloads / picks up jobs
  // started in another tab. Counts both analysis kinds — the badge is a single
  // "analyses running" indicator, not a deviation-only one.
  const runningCount =
    (data?.deviationJobs.filter(isRunning).length ?? 0) + (sdData?.surfaceDifferenceJobs.filter(isRunning).length ?? 0);

  return { runningCount };
}
